import React, { useState } from 'react';
import { devLog, devWarn, devError } from '../../utils/devLogger';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { AppTextField } from '../../customComponents/AppTextField';
import { AppEmailTextField } from '../../customComponents/AppEmailTextField';
import { AppPwdTextField, PwdFieldType } from '../../customComponents/AppPwdTextField';
import { AppAuthCodeTextField } from '../../customComponents/AppAuthCodeTextField';
import { FormTitle } from '../../customComponents/FormTitle';
import PrimaryButton from '../../components/PrimaryButton';
import CenteredLayout from '../../layout/CenteredLayout';
import PageContainerLayout from '../../layout/PageContainerLayout';
import ProjectAppbar from '../../customComponents/ProjectAppbar';
import FormContainer from '../../customComponents/FormContainer';
import { sendAuthCodeReset, verifyAuthCode, resetPassword } from '../../lib/api/user/userApi';
import { useLang } from '../../context/LangContext';

// 다국어 텍스트 객체
const findPasswordTexts = {
  ko: {
    title: '비밀번호 찾기',
    identityVerification: '본인 확인',
    newPasswordSetting: '새 비밀번호 설정',
    nameLabel: '이름',
    namePlaceholder: '이름을 입력해주세요',
    nameValidationError: '이름은 2글자 이상 입력해주세요',
    authButtonText: '인증하기',
    verifyButtonText: '인증확인',
    changePasswordButton: '비밀번호 변경',
    
    // 토스트 메시지
    authCodeSent: '인증번호가 발송되었습니다. 이메일을 확인해주세요.',
    userNotFound: '입력하신 이름과 이메일로 가입된 계정을 찾을 수 없습니다.',
    authCodeSendFailed: '인증번호 발송에 실패했습니다. 다시 시도해주세요.',
    authCodeSendFailedWithInfo: '인증번호 발송에 실패했습니다. 입력하신 정보를 확인해주세요.',
    authCodeVerified: '인증번호가 확인되었습니다.',
    authCodeIncorrect: '인증번호가 맞지 않습니다.',
    authCodeInvalid: '인증번호가 올바르지 않습니다. 다시 확인해주세요.',
    passwordChangeSuccess: '비밀번호가 성공적으로 변경되었습니다.',
    passwordChangeFailed: '비밀번호 변경에 실패했습니다. 다시 시도해주세요.'
  },
  en: {
    title: 'Find Password',
    identityVerification: 'Identity Verification',
    newPasswordSetting: 'Set New Password',
    nameLabel: 'Name',
    namePlaceholder: 'Please enter your name',
    nameValidationError: 'Please enter at least 2 characters for name',
    authButtonText: 'Verify',
    verifyButtonText: 'Confirm',
    changePasswordButton: 'Change Password',
    
    // 토스트 메시지
    authCodeSent: 'Verification code has been sent. Please check your email.',
    userNotFound: 'No account found with the entered name and email.',
    authCodeSendFailed: 'Failed to send verification code. Please try again.',
    authCodeSendFailedWithInfo: 'Failed to send verification code. Please check the entered information.',
    authCodeVerified: 'Verification code has been confirmed.',
    authCodeIncorrect: 'Verification code is incorrect.',
    authCodeInvalid: 'Verification code is invalid. Please check again.',
    passwordChangeSuccess: 'Password has been successfully changed.',
    passwordChangeFailed: 'Failed to change password. Please try again.'
  }
};

// FindPasswordContainer styles are now handled by PageContainerLayout

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CompleteButtonContainer = styled.div`
  width: 100%;
`;

function FindPassword() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const texts = findPasswordTexts[lang];
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [authCode, setAuthCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [isNameValid, setIsNameValid] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isAuthCodeValid, setIsAuthCodeValid] = useState(false);
  const [isNewPasswordValid, setIsNewPasswordValid] = useState(false);
  const [isNewPasswordConfirmValid, setIsNewPasswordConfirmValid] = useState(false);
  const [isAuthCodeSent, setIsAuthCodeSent] = useState(false);
  const [isAuthCodeVerified, setIsAuthCodeVerified] = useState(false);

  // 에러 메시지 파싱 함수 (Step1에서 가져옴)
  const parseErrorMessage = (error: any, defaultMessage: string): string => {
    // 중첩된 에러 구조 파싱
    if (error?.data?.error?.details?.details && Array.isArray(error.data.error.details.details)) {
      // validation 에러의 상세 메시지 추출
      const validationError = error.data.error.details.details[0];
      if (validationError?.message) {
        return validationError.message;
      }
    }
    
    if (error?.data?.error?.customMessage) {
      return error.data.error.customMessage;
    }
    
    if (error?.data?.message) {
      return error.data.message;
    }
    
    if (error?.message) {
      return error.message;
    }
    
    return defaultMessage;
  };

  const handleBack = () => {
    navigate(-1); // 이전 페이지로 이동
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    setName(value);
    setIsNameValid(value.trim().length >= 2); // 이름은 2글자 이상
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setEmail(e.target.value);
  };

  const handleAuthCodeChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setAuthCode(e.target.value);
    setIsAuthCodeVerified(false); // 인증번호가 변경되면 인증 확인 상태 초기화
  };

  const handleAuthRequest = async () => {
    try {
      const response = await sendAuthCodeReset({ 
        email: email.trim(), 
        name: name.trim() 
      });
      
      // 응답 데이터의 statusCode 확인 후 메시지 처리
      if (response.data.statusCode === 200) {
        toast.success(texts.authCodeSent);
        setIsAuthCodeSent(true);
        // 새로운 인증번호 발송 시 기존 인증 상태 초기화
        setAuthCode('');
        setIsAuthCodeValid(false);
        setIsAuthCodeVerified(false);
      } else if (response.data.statusCode === 404) {
        // 404 에러 (존재하지 않는 유저) 처리
        toast.error(texts.userNotFound);
      } else {
        // 기타 에러 응답 처리
        const errorMessage = response.data.message || texts.authCodeSendFailed;
        toast.error(errorMessage);
      }
    } catch (error: any) {
      const errorMessage = parseErrorMessage(error, texts.authCodeSendFailedWithInfo);
      toast.error(errorMessage);
    }
  };

  const handleVerifyAuthCode = async () => {
    try {
      devLog('인증번호 확인 요청:', { email, authCode: Number(authCode) });
      const response = await verifyAuthCode({ email: email.trim(), authCode: Number(authCode) });
      devLog('인증번호 확인 응답:', response);
      
      // 응답 데이터의 statusCode 확인 후 메시지 처리
      if (response.data.statusCode === 200) {
        toast.success(texts.authCodeVerified);
        setIsAuthCodeVerified(true); // 인증 확인 완료 상태로 변경
      } else if (response.data.statusCode === 401) {
        // 401 에러 (인증번호 불일치) 처리
        toast.error(texts.authCodeIncorrect);
        setIsAuthCodeVerified(false);
      } else {
        // 기타 에러 응답 처리
        const errorMessage = response.data.message || texts.authCodeInvalid;
        toast.error(errorMessage);
        setIsAuthCodeVerified(false);
      }
    } catch (error: any) {
      devError('인증번호 확인 실패:', error);
      
      const errorMessage = parseErrorMessage(error, texts.authCodeInvalid);
      toast.error(errorMessage);
      setIsAuthCodeVerified(false); // 인증 실패 시 false로 재설정
    }
  };

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNewPassword(e.target.value);
  };

  const handleNewPasswordConfirmChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNewPasswordConfirm(e.target.value);
  };

  const handleComplete = async () => {
    try {
      const response = await resetPassword({
        email: email.trim(),
        name: name.trim(),
        password: newPassword.trim()
      });

      // 응답 데이터의 statusCode 확인 후 메시지 처리
      if (response.data.statusCode === 200) {
        toast.success(texts.passwordChangeSuccess);
        navigate('/login');
      } else {
        // 200이 아닌 응답 처리
        const errorMessage = response.data.message || texts.passwordChangeFailed;
        toast.error(errorMessage);
      }
    } catch (error: any) {
      devError('비밀번호 변경 실패:', error);
      
      const errorMessage = parseErrorMessage(error, texts.passwordChangeFailed);
      toast.error(errorMessage);
    }
  };

  const isFormValid = isAuthCodeVerified && 
                     isNewPasswordValid && isNewPasswordConfirmValid && 
                     newPassword.trim() !== '' && newPasswordConfirm.trim() !== '';

  return (
    <CenteredLayout>
      <ProjectAppbar
        title={texts.title}
        showBackButton={true}
        onBack={handleBack}
      />
      <FormContainer>
        <PageContainerLayout>
            <FormGroup>
              <FormTitle>{isAuthCodeVerified ? texts.newPasswordSetting : texts.identityVerification}</FormTitle>
            
            {!isAuthCodeVerified && (
              <>
                <AppTextField
                  value={name}
                  onChange={handleNameChange}
                  label={texts.nameLabel}
                  placeholder={texts.namePlaceholder}
                  errorMessage={name && !isNameValid ? texts.nameValidationError : ''}
                  autoFocus={true}
                  autoComplete="name"
                />

                <AppEmailTextField
                  value={email}
                  onChange={handleEmailChange}
                  onValidationChange={setIsEmailValid}
                  showAuthButton={true}
                  onAuthRequest={handleAuthRequest}
                  authButtonText={texts.authButtonText}
                  language={lang}
                />
                
                {isAuthCodeSent && (
                  <AppAuthCodeTextField
                    value={authCode}
                    onChange={handleAuthCodeChange}
                    onValidationChange={setIsAuthCodeValid}
                    showVerifyButton={true}
                    onVerifyRequest={handleVerifyAuthCode}
                    verifyButtonText={texts.verifyButtonText}
                    language={lang}
                    autoFocus={true}
                    readOnly={isAuthCodeVerified}
                  />
                )}
              </>
            )}

            {isAuthCodeVerified && (
              <>
                <AppPwdTextField
                  value={newPassword}
                  onChange={handleNewPasswordChange}
                  fieldType={PwdFieldType.PASSWORD}
                  onValidationChange={setIsNewPasswordValid}
                  language={lang}
                  autoComplete="new-password"
                  autoFocus={true}
                />

                <AppPwdTextField
                  value={newPasswordConfirm}
                  onChange={handleNewPasswordConfirmChange}
                  fieldType={PwdFieldType.PASSWORD_CONFIRM}
                  onValidationChange={setIsNewPasswordConfirmValid}
                  originalPassword={newPassword}
                  language={lang}
                  autoComplete="new-password"
                />
              </>
            )}
          </FormGroup>

          {isAuthCodeVerified && (
            <CompleteButtonContainer>
              <PrimaryButton
                onClick={handleComplete}
                disabled={!isFormValid}
              >
                {texts.changePasswordButton}
              </PrimaryButton>
            </CompleteButtonContainer>
          )}
        </PageContainerLayout>
      </FormContainer>
    </CenteredLayout>
  );
}

export default FindPassword;
